import * as React from 'react'
import { ReactElement, useEffect, useMemo } from 'react'
import Form from '../../../../components/Form/Form'
import { Card, Field, makeStyles, mergeClasses, MessageBar, Text, tokens } from '@fluentui/react-components'
import { useFormikContext } from 'formik'
import FileSuggestionDataGrid from '../../../../components/FileMessage/FileSuggestionDataGrid'
import { SelectableEmail } from '../../../../hooks/email/useProcessEmail'
import { FileSuggestionMatchDto } from '../../../../components/FileMessage/FileMessageForm'
import useEntity from '../../../../hooks/useEntity'
import { EmailAddressDto, MessageDto } from '../../../../context/MessageContext'
import { Recipient } from '@microsoft/microsoft-graph-types'
import { appInsightsReact } from '../../../../pages/App'
import { SeverityLevel } from '@microsoft/applicationinsights-common'
import { ProcessEmailFormProps, ProcessEmailValues } from './ProcessEmailForm'
import { EmailProcessingAction } from '../../../config/emailProcessingConfig'
import { formatFullDateTime } from '../../../../helpers/dateHelper'
import { useTranslation } from 'react-i18next'
import PersonAvatar from '../../../../components/PersonAvatar'
import FilePicker from '../../../../components/TagPicker/FilePicker'

const useMessageHeaderStyles = makeStyles({
    card: {
        marginBottom: tokens.spacingVerticalM,
    },
    container: {
        display: 'grid',
        gridTemplateColumns: '1fr 9fr 2fr',
        // shorthands.gap() took (columnGap, rowGap); the CSS shorthand takes them the other way around.
        gap: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
        margin: `${tokens.spacingVerticalM} 0`,
        alignItems: 'center',
    },
    description: {
        display: 'grid',
    },
    datetime: {
        display: 'grid',
        textAlign: 'end',
    },
})

export default function FormContent(props: ProcessEmailFormProps): ReactElement {
    const { values, setFieldValue, submitForm, isSubmitting } = useFormikContext<ProcessEmailValues>()
    const styles = useMessageHeaderStyles()
    const { t: translate } = useTranslation()

    const singleEmail: SelectableEmail = useMemo<SelectableEmail>(() => {
        if (props.selection.getSelectedCount() === 1) {
            return props.selection.getSelection().find(() => true)
        }
        return undefined
    }, [props.selection])

    const [suggestions] = useEntity<FileSuggestionMatchDto[]>(
        () => ({
            method: 'POST',
            url: '/email/file-suggest',
            data: props.selection.getSelection().map(email => mapSelectableEmail(email)),
        }),
        false,
        result => result.data,
        [],
        undefined,
        [props.selection],
    )

    useEffect(() => {
        props.setSubmitForm?.(submitForm)
        props.setIsSubmitting?.(isSubmitting)
    }, [])

    useEffect(() => {
        props.setIsSubmitting?.(isSubmitting)
    }, [isSubmitting])

    useEffect(() => {
        if (suggestions.rejected) {
            appInsightsReact.trackException({
                exception: suggestions.error,
                severityLevel: SeverityLevel.Error,
                properties: {
                    reactComponent: 'Dialog.ProcessEmail.FormContent',
                    emailId: props.selection?.getSelection()?.map(email => email.id),
                },
            })
        }
    }, [suggestions])

    if (EmailProcessingAction.Ignore === props.action) {
        return <Text>{translate('email.filing.not_file.description')}</Text>
    }

    return (
        <Form globalErrors={props.globalErrors}>
            {singleEmail && (
                <Card className={styles.card}>
                    <Text size={500}>{singleEmail.subject}</Text>
                    <div className={mergeClasses(styles.container)}>
                        <PersonAvatar name={singleEmail.sender?.emailAddress?.name} color="colorful" size={48} />
                        <div className={mergeClasses(styles.description)}>
                            <Text>{singleEmail.sender?.emailAddress?.name || singleEmail.sender?.emailAddress?.address}</Text>
                            <Text>
                                {translate('email.filing.to')}
                                :&nbsp;
                                {singleEmail.recipient ||
                                    singleEmail.recipients
                                        ?.map(recipient => recipient.emailAddress?.name || recipient.emailAddress?.address)
                                        .join(', ')}
                            </Text>
                        </div>
                        <Text>{formatFullDateTime(singleEmail.date)}</Text>
                    </div>
                </Card>
            )}

            <MessageBar intent={'info'} layout={'multiline'}>
                {translate('email.filing.file.description')}
            </MessageBar>
            <FileSuggestionDataGrid
                suggestions={suggestions}
                selectedItems={values.references}
                onSelectionChange={selection => setFieldValue('references', selection)}
            />
            <Field label={translate('email.filing.file')}>
                <FilePicker
                    selectedOptions={values.references}
                    getEntityValue={entity => entity.reference}
                    onOptionsSelect={(_, selectedOptions) => setFieldValue('references', selectedOptions)}
                    filter={() => true}
                />
            </Field>
        </Form>
    )
}

export const mapEmailUser = (recipient: Recipient): EmailAddressDto => ({
    name: recipient.emailAddress?.name,
    address: recipient.emailAddress?.address,
})
export const mapSelectableEmail = (item: SelectableEmail): MessageDto => ({
    internetMessageId: item.internetMessageId,
    conversationId: item.conversationId,
    subject: item.subject,
    sender: mapEmailUser(item.sender),
    recipients: item.recipients.map(recipient => mapEmailUser(recipient)),
})
